"use client";

import { Suspense, useActionState } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { signupAction } from "@/app/actions/auth";
import { GoogleButton } from "@/components/google-button";
import { BrandMark } from "@/components/brand";

function Logo() {
  return (
    <Link href="/" className="flex items-center" aria-label="Clearline home">
      <BrandMark height={26} priority />
    </Link>
  );
}

export default function SignupPage() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-[#080808]" />}>
      <SignupForm />
    </Suspense>
  );
}

function SignupForm() {
  const searchParams = useSearchParams();
  const next = searchParams.get("next") ?? "/";

  const [state, formAction] = useActionState(signupAction, undefined);

  return (
    <div className="cl-scope">
      {/* The cl-* tokens and display/mono fonts from the Base44 build are not
          registered in this project's Tailwind theme, so we scope them here
          with the exact hex values from Base44's compiled CSS. */}
      <style>{authTokens}</style>

      <main className="min-h-screen bg-cl-bg flex flex-col items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm">
          <div className="flex justify-center mb-10">
            <Logo />
          </div>

          <h1 className="font-display text-3xl text-cl-text tracking-tight text-center mb-2">
            Create your account
          </h1>
          <p className="text-cl-muted text-sm text-center mb-8">
            Join the wholesale hub and start moving lots.
          </p>

          <div className="bg-cl-surface border border-cl-border rounded-2xl p-8">
            <GoogleButton next={next} />

            <div className="flex items-center gap-3 my-6">
              <div className="h-px flex-1 bg-cl-border" />
              <span className="text-[10px] font-mono text-cl-muted uppercase tracking-widest">
                or
              </span>
              <div className="h-px flex-1 bg-cl-border" />
            </div>

            <form action={formAction} className="space-y-6">
              <input type="hidden" name="next" value={next} />

              <div>
                <label className="block text-[10px] font-mono text-cl-muted uppercase tracking-widest mb-2">
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  placeholder="Your name"
                  autoComplete="name"
                  className="w-full bg-transparent border-0 border-b border-cl-border pb-2 text-cl-text text-base focus:border-cl-green focus:outline-none transition-colors"
                />
              </div>

              <div>
                <label className="block text-[10px] font-mono text-cl-muted uppercase tracking-widest mb-2">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder="you@company.com"
                  required
                  autoComplete="email"
                  className="w-full bg-transparent border-0 border-b border-cl-border pb-2 text-cl-text text-base focus:border-cl-green focus:outline-none transition-colors"
                />
              </div>

              <div>
                <label className="block text-[10px] font-mono text-cl-muted uppercase tracking-widest mb-2">
                  Password
                </label>
                <input
                  type="password"
                  name="password"
                  placeholder="At least 8 characters"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className="w-full bg-transparent border-0 border-b border-cl-border pb-2 text-cl-text text-base focus:border-cl-green focus:outline-none transition-colors"
                />
              </div>

              {state?.error ? (
                <p className="text-sm text-red-400">{state.error}</p>
              ) : null}

              <button
                type="submit"
                className="w-full bg-cl-green text-cl-bg font-mono text-sm uppercase tracking-widest py-3 rounded-lg hover:bg-cl-green/90 transition-colors"
              >
                Create account
              </button>
            </form>
          </div>

          <p className="text-center text-sm text-cl-muted mt-8">
            Already have an account?{" "}
            <Link href="/login" className="text-cl-green hover:underline">
              Sign in
            </Link>
          </p>

          <p className="text-center text-xs text-cl-muted mt-6 opacity-70">
            Demo credentials:{" "}
            <span className="font-mono">member@clearline.test</span>
            {" / "}
            <span className="font-mono">clearline</span>
          </p>
        </div>
      </main>
    </div>
  );
}

// Scoped cl-* token definitions (exact hex from Base44's compiled CSS).
const authTokens = `
.cl-scope { font-family: Inter, ui-sans-serif, system-ui, sans-serif; }
.cl-scope .font-display { font-family: var(--font-space-grotesk), "Space Grotesk", sans-serif; }
.cl-scope .font-mono { font-family: ui-monospace, "SF Mono", monospace; }
.cl-scope.bg-cl-bg, .cl-scope .bg-cl-bg { background-color: #080808; }
.cl-scope .bg-cl-surface { background-color: #0e0e0e; }
.cl-scope .bg-cl-green { background-color: #22c55e; }
.cl-scope .hover\\:bg-cl-green\\/90:hover { background-color: rgba(34, 197, 94, 0.9); }
.cl-scope .text-cl-text { color: #f0f0f0; }
.cl-scope .text-cl-muted { color: #888888; }
.cl-scope .text-cl-green { color: #22c55e; }
.cl-scope .text-cl-bg { color: #080808; }
.cl-scope .border-cl-border { border-color: #1c1c1c; }
.cl-scope .focus\\:border-cl-green:focus { border-color: #22c55e; }
.cl-scope input::placeholder { color: rgba(136, 136, 136, 0.35); }
`;
